import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type {
  PromotionalAdErrorKey,
  StorePromotionalAdWithPreview,
  TranslationKey,
} from '@dukanti/shared';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export interface DeletePromotionalAdDialogProps {
  readonly ad?: StorePromotionalAdWithPreview | null;
  readonly adTitle?: string;
  readonly open: boolean;
  readonly isDeleting?: boolean;
  readonly errorKey?: PromotionalAdErrorKey | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: () => void;
}

export function DeletePromotionalAdDialog({
  ad,
  adTitle,
  open,
  isDeleting = false,
  errorKey,
  onOpenChange,
  onConfirm,
}: DeletePromotionalAdDialogProps) {
  const { t, dir } = useTranslation();
  const name =
    adTitle ||
    ad?.title?.trim() ||
    ad?.alt_text?.trim() ||
    t('admin.promotionalAds.list.untitled' as TranslationKey);
  const canDelete = Boolean(ad || adTitle);

  return (
    <AlertDialog open={open} onOpenChange={(next) => !isDeleting && onOpenChange(next)}>
      <AlertDialogContent dir={dir} data-testid="delete-promotional-ad-dialog">
        <div className="bg-terracotta/10 text-terracotta mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
          <Trash2 className="size-6" aria-hidden />
        </div>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center">
            {t('admin.promotionalAds.delete.title' as TranslationKey)}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {t('admin.promotionalAds.delete.description' as TranslationKey, { name })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorKey ? (
          <div
            role="alert"
            data-error-key={errorKey}
            className="border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card flex items-start gap-2 border p-3 text-start text-sm"
          >
            <AlertCircle
              aria-hidden="true"
              className="text-terracotta mt-0.5 size-4 shrink-0"
              strokeWidth={1.75}
            />
            <p>{t(errorKey as TranslationKey)}</p>
          </div>
        ) : null}

        <AlertDialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={isDeleting}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isDeleting || !canDelete}
            onClick={onConfirm}
            aria-busy={isDeleting || undefined}
          >
            {isDeleting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span>{t('admin.promotionalAds.delete.confirm' as TranslationKey)}</span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
